/**
 * TwoDCanvas - Lightweight 2D canvas viewer for product profile section editing.
 * Adapted from Customizer's TwoDViewer (packages/2d-handler/src/core/TwoDViewer.js).
 *
 * Renders a product image with section overlays for admin profile setup.
 * Supports rectangle, circle, polygon shapes with move/resize/rotate interaction.
 */

import getMaterialTexture from './materialTextures'
import { materialColorToCss } from './materialColor'

export type ShapeType = 'rectangle' | 'circle' | 'polygon'

export interface Section2D {
  sectionId: string
  name: string
  offsetX: number   // horizontal offset from center in mm
  offsetY: number   // vertical offset from bottom to center in mm
  width: number     // section width in mm
  height: number    // section height in mm
  imageUrl?: string // optional per-section image URL
  shapeType: ShapeType  // defaults to 'rectangle'
  rotation: number      // degrees
  radius?: number       // circle only, in mm
  vertices?: { x: number; y: number }[]  // polygon only, mm relative to section center
  materialType?: string      // 'steel' | 'gold' | 'sandblast' | 'anodized' | 'wood_burn' | 'glass_etch' | 'color' | 'custom'
  materialFile?: string      // custom material only: url of the uploaded pattern tile
  materialColor?: string     // custom material only: picked colour, used when there is no pattern
  engravingTint?: number     // 0-1, brightness of engraved area
  overlayOpacity?: number    // 0-1, section overlay blend strength
}

interface TargetObject2D {
  width: number     // physical width in mm
  height: number    // physical height in mm
}

interface ViewTransform {
  x: number         // pan offset X in screen pixels
  y: number         // pan offset Y in screen pixels
  scale: number     // zoom level
}

interface SectionBounds {
  x: number
  y: number
  width: number
  height: number
}

interface CalibrationState {
  active: boolean
  startX: number | null
  startY: number | null
  endX: number | null
  endY: number | null
}

type HandleId = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w'

interface HitResult {
  hit: 'body' | 'handle' | 'rotate' | 'vertex' | 'none'
  handleId?: HandleId
  vertexIndex?: number
}

type InteractionMode = 'idle' | 'moving' | 'resizing' | 'rotating'

interface InteractionState {
  mode: InteractionMode
  handleId: HandleId | null
  vertexIndex: number | null
  startImagePt: { x: number; y: number }
  startSection: Section2D
}

type DrawingMode = 'draw-rectangle' | 'draw-circle' | 'draw-polygon' | null

interface DrawResult {
  shapeType: ShapeType
  offsetX: number
  offsetY: number
  width: number
  height: number
  radius?: number
  vertices?: { x: number; y: number }[]
}

type SectionChangeCallback = (section: Section2D) => void
type ScreenHandleShape = 'circle' | 'square'

interface ScreenHandleStyle {
  shape: ScreenHandleShape
  fillStyle: string
  strokeStyle?: string
}

const SELECTED_SECTION_STROKE_WIDTH_PX = 1.5
const CALIBRATION_LABEL_FONT_SIZE_PX = 12
const HANDLE_SIZE_CANVAS_RATIO = 0.02
const HANDLE_SIZE_MIN_PX = 8
const HANDLE_SIZE_MAX_PX = 14
const HANDLE_HIT_RADIUS_PX = 8
const ROTATION_HANDLE_DISTANCE_CANVAS_RATIO = 0.03
const ROTATION_HANDLE_DISTANCE_MIN_PX = 12
const ROTATION_HANDLE_DISTANCE_MAX_PX = 18
const DRAWING_MAGNIFIER_SIZE_PX = 75
const DRAWING_MAGNIFIER_ZOOM = 2.5

const clamp = (value: number, min: number, max: number) => (
  Math.min(Math.max(value, min), max)
)

/**
 * Colour + opacity an engraved mark takes on for a given material.
 *
 * Kept numerically in sync with the customizer's TwoDViewer.getMaterialTintColor
 * (packages/2d-handler) so what the admin previews is what the shopper sees.
 * Returns null for 'color' mode, where artwork keeps its own colours and there
 * is no material tint to show.
 */
const getMaterialAppearance = (
  materialType: string,
  engravingTint: number,
  overlayOpacity: number,
  materialColor?: string
): { color: string; alpha: number } | null => {
  if (materialType === 'color') return null

  // A custom material with a picked colour renders that hue at the engraving
  // tint's brightness, matching the multiply the 3D shader performs.
  if (materialType === 'custom') {
    const css = materialColorToCss(materialColor, engravingTint)
    if (css) return { color: css, alpha: clamp(overlayOpacity, 0, 1) }
  }

  // A laser mark is burned into the substrate, not laid over it: whatever
  // material, pattern or colour was picked here is what the shopper has to see,
  // with none of the product photo showing through it. So the filled and burned
  // materials are fully opaque, and Overlay Opacity is the only control that
  // dials that back. sandblast and glass_etch are the exception — etched glass
  // really is see-through, so their alpha stays tint-driven (frost strength).
  // Kept in sync with the customizer's TwoDViewer.getMaterialTintColor.
  const t = engravingTint
  const tint = Math.round(t * 255)
  const materials: Record<string, { color: string; alpha: number }> = {
    steel:      { color: `rgb(${tint}, ${tint}, ${tint})`, alpha: 1 },
    gold:       { color: `rgb(${Math.min(255, tint + 45)}, ${Math.round(tint * 0.8 + 25)}, ${Math.round(tint * 0.25)})`, alpha: 1 },
    sandblast:  { color: 'rgb(255, 255, 255)', alpha: 0.6 + t * 0.4 },
    anodized:   { color: `rgb(${tint}, ${tint}, ${Math.min(255, tint + 20)})`, alpha: 1 },
    wood_burn:  { color: `rgb(${Math.round(tint * 0.4 + 60)}, ${Math.round(tint * 0.2 + 20)}, ${Math.round(tint * 0.1)})`, alpha: 1 },
    glass_etch: { color: 'rgb(255, 255, 255)', alpha: 0.45 + t * 0.4 },
    // Custom paints the uploaded tile over this, so the colour here only shows
    // while the pattern is still loading (or if it failed to load).
    custom:     { color: `rgb(${tint}, ${tint}, ${tint})`, alpha: 1 },
  }

  const material = materials[materialType] || materials.steel
  return { color: material.color, alpha: clamp(material.alpha * overlayOpacity, 0, 1) }
}

class TwoDCanvas {
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private viewTransform: ViewTransform = { x: 0, y: 0, scale: 1 }
  private productImage: HTMLImageElement | null = null
  private productImageLoaded = false
  private sections: Section2D[] = []
  private sectionImages: Map<string, HTMLImageElement> = new Map()
  private materialPatterns: Map<string, HTMLImageElement> = new Map()
  private selectedSectionId: string | null = null
  private targetObject: TargetObject2D = { width: 200, height: 200 }
  private animFrameId: number | null = null
  private needsRender = true

  // Calibration state
  private calibrationState: CalibrationState = {
    active: false, startX: null, startY: null, endX: null, endY: null
  }
  private calibrationImagePixelsPerCanvasUnit = 1
  private isDraggingCalibration = false
  private onCalibrationComplete: ((pixelLength: number) => void) | null = null

  // Interaction state
  private interactionState: InteractionState | null = null
  private onSectionChange: SectionChangeCallback | null = null

  // Drawing state
  private drawingMode: DrawingMode = null
  private drawingPoints: { x: number; y: number }[] = [] // image-space points
  private drawingCurrent: { x: number; y: number } | null = null // current mouse pos during draw
  private isDrawingMagnifierActive = false
  private isPointerInsideCanvas = false
  private onDrawComplete: ((result: DrawResult) => void) | null = null
  private onDrawEnd: (() => void) | null = null

  // Resize observer for detecting visibility changes
  private resizeObserver: ResizeObserver | null = null

  // Bound handlers for cleanup
  private boundHandleResize: () => void
  private boundHandleMouseDown: (e: MouseEvent) => void
  private boundHandleMouseMove: (e: MouseEvent) => void
  private boundHandleMouseUp: (e: MouseEvent) => void
  private boundHandleMouseEnter: () => void
  private boundHandleMouseLeave: () => void
  private boundHandleDblClick: (e: MouseEvent) => void
  private boundHandleKeyDown: (e: KeyboardEvent) => void
  private boundHandleKeyUp: (e: KeyboardEvent) => void

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas
    this.ctx = canvas.getContext('2d')!

    this.boundHandleResize = this.handleResize.bind(this)
    this.boundHandleMouseDown = this.handleMouseDown.bind(this)
    this.boundHandleMouseMove = this.handleMouseMove.bind(this)
    this.boundHandleMouseUp = this.handleMouseUp.bind(this)
    this.boundHandleMouseEnter = this.handleMouseEnter.bind(this)
    this.boundHandleMouseLeave = this.handleMouseLeave.bind(this)
    this.boundHandleDblClick = this.handleDblClick.bind(this)
    this.boundHandleKeyDown = this.handleKeyDown.bind(this)
    this.boundHandleKeyUp = this.handleKeyUp.bind(this)

    this.setupEventListeners()
    this.handleResize()
    this.startRenderLoop()

    // Detect visibility changes (e.g., parent toggling display:none)
    this.resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const { width, height } = entry.contentRect
      if (width > 0 && height > 0) {
        this.handleResize()
        this.fitImageToView()
      }
    })
    this.resizeObserver.observe(this.canvas)
  }

  // --- Public API ---

  async loadProductImage(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        this.productImage = img
        this.productImageLoaded = true
        this.fitImageToView()
        this.needsRender = true
        resolve()
      }
      img.onerror = reject
      img.src = src
    })
  }

  setTargetObject(width: number, height: number): void {
    this.targetObject = { width, height }
    this.needsRender = true
  }

  setSections(sections: Section2D[], imageSections: Section2D[] = sections): void {
    this.sections = sections
    this.loadSectionImages(imageSections)
    this.loadMaterialPatterns(sections)
    this.needsRender = true
  }

  /** Cache custom material tiles by url — several sections may share one. */
  private loadMaterialPatterns(sections: Section2D[]): void {
    const urls = new Set(
      sections
        .filter(s => s.materialType === 'custom' && s.materialFile)
        .map(s => s.materialFile as string)
    )

    for (const url of this.materialPatterns.keys()) {
      if (!urls.has(url)) {
        this.materialPatterns.delete(url)
      }
    }

    for (const url of urls) {
      if (this.materialPatterns.has(url)) continue

      const img = new Image()
      img.crossOrigin = 'anonymous'
      this.materialPatterns.set(url, img)
      img.onload = () => {
        this.needsRender = true
      }
      img.src = url
    }
  }

  private loadSectionImages(sections: Section2D[]): void {
    const sectionsWithImage = new Set(sections.filter(s => s.imageUrl).map(s => s.sectionId))
    for (const id of this.sectionImages.keys()) {
      if (!sectionsWithImage.has(id)) {
        this.sectionImages.delete(id)
      }
    }

    for (const section of sections) {
      if (!section.imageUrl) continue
      const existing = this.sectionImages.get(section.sectionId)
      if (existing && existing.src === section.imageUrl) continue

      const img = new Image()
      img.crossOrigin = 'anonymous'
      this.sectionImages.set(section.sectionId, img)
      img.onload = () => {
        this.needsRender = true
      }
      img.src = section.imageUrl
    }
  }

  setSelectedSection(sectionId: string | null): void {
    this.selectedSectionId = sectionId
    this.needsRender = true
  }

  setOnSectionChange(callback: SectionChangeCallback | null): void {
    this.onSectionChange = callback
  }

  setOnDrawEnd(callback: (() => void) | null): void {
    this.onDrawEnd = callback
  }

  /** Enter calibration mode. Callback and label use actual image pixels. */
  startCalibration(
    onComplete: (pixelLength: number) => void,
    imagePixelsPerCanvasUnit = 1
  ): void {
    this.calibrationState = { active: true, startX: null, startY: null, endX: null, endY: null }
    this.calibrationImagePixelsPerCanvasUnit =
      Number.isFinite(imagePixelsPerCanvasUnit) && imagePixelsPerCanvasUnit > 0
        ? imagePixelsPerCanvasUnit
        : 1
    this.isDraggingCalibration = false
    this.onCalibrationComplete = onComplete
    this.canvas.style.cursor = 'crosshair'
    this.needsRender = true
  }

  /** Exit calibration mode. */
  cancelCalibration(): void {
    this.calibrationState = { active: false, startX: null, startY: null, endX: null, endY: null }
    this.calibrationImagePixelsPerCanvasUnit = 1
    this.isDraggingCalibration = false
    this.onCalibrationComplete = null
    this.canvas.style.cursor = ''
    this.needsRender = true
  }

  isCalibrating(): boolean {
    return this.calibrationState.active
  }

  /** Dimensions of the coordinate space currently rendered on the canvas. */
  getCanvasBaseSize(): { width: number; height: number } {
    return this.getBaseSize()
  }

  /** Enter drawing mode for a specific shape. */
  startDrawing(mode: 'draw-rectangle' | 'draw-circle' | 'draw-polygon', onComplete: (result: DrawResult) => void): void {
    this.drawingMode = mode
    this.drawingPoints = []
    this.drawingCurrent = null
    this.isDrawingMagnifierActive = false
    this.onDrawComplete = onComplete
    this.canvas.style.cursor = 'crosshair'
    this.needsRender = true
  }

  cancelDrawing(): void {
    const wasDrawing = this.drawingMode !== null
    this.drawingMode = null
    this.drawingPoints = []
    this.drawingCurrent = null
    this.isDrawingMagnifierActive = false
    this.onDrawComplete = null
    this.canvas.style.cursor = ''
    this.needsRender = true
    if (wasDrawing && this.onDrawEnd) {
      this.onDrawEnd()
    }
  }

  isDrawing(): boolean {
    return this.drawingMode !== null
  }

  private getBaseSize(): { width: number; height: number } {
    if (this.productImage && this.productImageLoaded && this.productImage.width > 0) {
      return { width: this.productImage.width, height: this.productImage.height }
    }
    return { width: this.targetObject.width, height: this.targetObject.height }
  }

  /** Visual controls scale with the canvas viewport, independently of millimeter calibration. */
  private getHandleMetrics(): {
    visualSizePx: number
    hitRadius: number
    rotationDistance: number
  } {
    const viewScale = Math.max(this.viewTransform.scale, Number.EPSILON)
    const canvasRect = this.canvas.getBoundingClientRect()
    const canvasShortSide = Math.max(1, Math.min(canvasRect.width, canvasRect.height))
    const visualSizePx = clamp(
      canvasShortSide * HANDLE_SIZE_CANVAS_RATIO,
      HANDLE_SIZE_MIN_PX,
      HANDLE_SIZE_MAX_PX
    )
    const rotationDistancePx = clamp(
      canvasShortSide * ROTATION_HANDLE_DISTANCE_CANVAS_RATIO,
      ROTATION_HANDLE_DISTANCE_MIN_PX,
      ROTATION_HANDLE_DISTANCE_MAX_PX
    )

    return {
      visualSizePx,
      hitRadius: Math.max(HANDLE_HIT_RADIUS_PX, visualSizePx / 2) / viewScale,
      rotationDistance: rotationDistancePx / viewScale,
    }
  }

  fitImageToView(): void {
    const base = this.getBaseSize()
    if (!base.width || !base.height) return

    const rect = this.canvas.getBoundingClientRect()
    const clientWidth = rect.width
    const clientHeight = rect.height

    if (!clientWidth || !clientHeight) {
      return // Preserve existing viewTransform; ResizeObserver will re-trigger when visible
    }

    const padding = 40
    const availableWidth = clientWidth - padding * 2
    const availableHeight = clientHeight - padding * 2

    const imageAspect = base.width / base.height
    const viewAspect = availableWidth / availableHeight

    let scale: number
    if (imageAspect > viewAspect) {
      scale = availableWidth / base.width
    } else {
      scale = availableHeight / base.height
    }

    const newX = (clientWidth - base.width * scale) / 2
    const newY = (clientHeight - base.height * scale) / 2

    if (isFinite(scale) && isFinite(newX) && isFinite(newY) && scale > 0) {
      this.viewTransform = { x: newX, y: newY, scale }
    } else {
      this.viewTransform = { x: 0, y: 0, scale: 1 }
    }

    this.needsRender = true
  }

  dispose(): void {
    this.cancelCalibration()
    this.cancelDrawing()
    this.stopRenderLoop()
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    window.removeEventListener('resize', this.boundHandleResize)
    this.canvas.removeEventListener('mousedown', this.boundHandleMouseDown)
    this.canvas.removeEventListener('mousemove', this.boundHandleMouseMove)
    this.canvas.removeEventListener('mouseup', this.boundHandleMouseUp)
    this.canvas.removeEventListener('mouseenter', this.boundHandleMouseEnter)
    this.canvas.removeEventListener('mouseleave', this.boundHandleMouseLeave)
    this.canvas.removeEventListener('dblclick', this.boundHandleDblClick)
    document.removeEventListener('keydown', this.boundHandleKeyDown)
    document.removeEventListener('keyup', this.boundHandleKeyUp)
  }

  // --- Coordinate Conversion ---

  private screenToImage(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect()
    const x = (clientX - rect.left - this.viewTransform.x) / this.viewTransform.scale
    const y = (clientY - rect.top - this.viewTransform.y) / this.viewTransform.scale
    return { x, y }
  }

  private getImageScale(): number {
    const base = this.getBaseSize()
    if (!base.width || !this.targetObject.width) return 1
    return base.width / this.targetObject.width
  }

  /** Convert image-space coordinates to mm world coordinates. */
  private imageToMm(imageX: number, imageY: number): { mmX: number; mmY: number } {
    const base = this.getBaseSize()
    const scale = this.getImageScale()
    const baseCenterX = base.width / 2
    const scaledTargetHeight = this.targetObject.height * scale
    const targetAreaTopY = (base.height - scaledTargetHeight) / 2

    const mmX = (imageX - baseCenterX) / scale
    const mmY = this.targetObject.height - (imageY - targetAreaTopY) / scale

    return { mmX, mmY }
  }

  private getSectionBounds(section: Section2D): SectionBounds | null {
    const base = this.getBaseSize()
    if (!base.width || !base.height) return null

    const scale = this.getImageScale()

    const scaledWidth = section.width * scale
    const scaledHeight = section.height * scale

    const baseCenterX = base.width / 2
    const sectionCenterX = baseCenterX + (section.offsetX * scale)
    const x = sectionCenterX - scaledWidth / 2

    const scaledTargetHeight = this.targetObject.height * scale
    const targetAreaTopY = (base.height - scaledTargetHeight) / 2
    const sectionTopInTarget = this.targetObject.height - section.offsetY - section.height / 2
    const y = targetAreaTopY + sectionTopInTarget * scale

    return { x, y, width: scaledWidth, height: scaledHeight }
  }

  // --- Hit Testing ---

  private hitTest(imageX: number, imageY: number): HitResult {
    const section = this.sections.find(s => s.sectionId === this.selectedSectionId)
    if (!section) return { hit: 'none' }

    const bounds = this.getSectionBounds(section)
    if (!bounds) return { hit: 'none' }

    const handleMetrics = this.getHandleMetrics()
    const hitRadius = handleMetrics.hitRadius
    const cx = bounds.x + bounds.width / 2
    const cy = bounds.y + bounds.height / 2
    const rotation = (section.rotation || 0) * Math.PI / 180

    // Transform test point into section's local coordinate space (undo rotation)
    let lx = imageX
    let ly = imageY
    if (rotation !== 0) {
      const cos = Math.cos(-rotation)
      const sin = Math.sin(-rotation)
      const dx = imageX - cx
      const dy = imageY - cy
      lx = cx + dx * cos - dy * sin
      ly = cy + dx * sin + dy * cos
    }

    // Rotation handle (above top center)
    const rotHandleX = cx
    const rotHandleY = bounds.y - handleMetrics.rotationDistance
    if (Math.abs(lx - rotHandleX) < hitRadius && Math.abs(ly - rotHandleY) < hitRadius) {
      return { hit: 'rotate' }
    }

    // Polygon vertex handles (check before rectangular handles)
    const shapeType = section.shapeType || 'rectangle'
    if (shapeType === 'polygon' && section.vertices && section.vertices.length >= 3) {
      const imgScale = this.getImageScale()
      for (let i = 0; i < section.vertices.length; i++) {
        const v = section.vertices[i]
        const vx = cx + v.x * imgScale
        const vy = cy - v.y * imgScale
        if (Math.abs(lx - vx) < hitRadius && Math.abs(ly - vy) < hitRadius) {
          return { hit: 'vertex', vertexIndex: i }
        }
      }
    }

    // Corner + edge handles (for rectangle/circle, and as fallback for polygon bounding box)
    if (shapeType !== 'polygon') {
      const handles: { id: HandleId; x: number; y: number }[] = [
        { id: 'nw', x: bounds.x, y: bounds.y },
        { id: 'ne', x: bounds.x + bounds.width, y: bounds.y },
        { id: 'sw', x: bounds.x, y: bounds.y + bounds.height },
        { id: 'se', x: bounds.x + bounds.width, y: bounds.y + bounds.height },
        { id: 'n', x: cx, y: bounds.y },
        { id: 's', x: cx, y: bounds.y + bounds.height },
        { id: 'w', x: bounds.x, y: cy },
        { id: 'e', x: bounds.x + bounds.width, y: cy },
      ]

      for (const h of handles) {
        if (Math.abs(lx - h.x) < hitRadius && Math.abs(ly - h.y) < hitRadius) {
          return { hit: 'handle', handleId: h.id }
        }
      }
    }

    // Body check
    if (this.isPointInsideSection(lx, ly, section, bounds)) {
      return { hit: 'body' }
    }

    return { hit: 'none' }
  }

  private isPointInsideSection(lx: number, ly: number, section: Section2D, bounds: SectionBounds): boolean {
    const shapeType = section.shapeType || 'rectangle'

    if (shapeType === 'rectangle') {
      return lx >= bounds.x && lx <= bounds.x + bounds.width &&
             ly >= bounds.y && ly <= bounds.y + bounds.height
    }

    if (shapeType === 'circle') {
      const cx = bounds.x + bounds.width / 2
      const cy = bounds.y + bounds.height / 2
      const r = Math.min(bounds.width, bounds.height) / 2
      const dx = lx - cx
      const dy = ly - cy
      return (dx * dx + dy * dy) <= (r * r)
    }

    if (shapeType === 'polygon' && section.vertices && section.vertices.length >= 3) {
      // Convert vertices to image space and do ray-casting
      const imgScale = this.getImageScale()
      const cx = bounds.x + bounds.width / 2
      const cy = bounds.y + bounds.height / 2
      const pts = section.vertices.map(v => ({
        x: cx + v.x * imgScale,
        y: cy - v.y * imgScale,
      }))
      return this.pointInPolygon(lx, ly, pts)
    }

    // Fallback: bounding box
    return lx >= bounds.x && lx <= bounds.x + bounds.width &&
           ly >= bounds.y && ly <= bounds.y + bounds.height
  }

  private pointInPolygon(px: number, py: number, pts: { x: number; y: number }[]): boolean {
    let inside = false
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      const xi = pts[i].x, yi = pts[i].y
      const xj = pts[j].x, yj = pts[j].y
      if (((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi)) {
        inside = !inside
      }
    }
    return inside
  }

  // --- Mouse Event Handling ---

  private setupEventListeners(): void {
    window.addEventListener('resize', this.boundHandleResize)
    this.canvas.addEventListener('mousedown', this.boundHandleMouseDown)
    this.canvas.addEventListener('mousemove', this.boundHandleMouseMove)
    this.canvas.addEventListener('mouseup', this.boundHandleMouseUp)
    this.canvas.addEventListener('mouseenter', this.boundHandleMouseEnter)
    this.canvas.addEventListener('mouseleave', this.boundHandleMouseLeave)
    this.canvas.addEventListener('dblclick', this.boundHandleDblClick)
    document.addEventListener('keydown', this.boundHandleKeyDown)
    document.addEventListener('keyup', this.boundHandleKeyUp)
  }

  private handleMouseDown(e: MouseEvent): void {
    const pt = this.screenToImage(e.clientX, e.clientY)

    // Priority 1: Calibration
    if (this.calibrationState.active) {
      this.calibrationState.startX = pt.x
      this.calibrationState.startY = pt.y
      this.calibrationState.endX = pt.x
      this.calibrationState.endY = pt.y
      this.isDraggingCalibration = true
      this.needsRender = true
      return
    }

    // Priority 2: Drawing mode
    if (this.drawingMode) {
      this.drawingCurrent = pt
      this.handleDrawingMouseDown(pt)
      return
    }

    // Priority 3: Section interaction
    if (this.selectedSectionId) {
      const result = this.hitTest(pt.x, pt.y)
      if (result.hit === 'none') return

      const section = this.sections.find(s => s.sectionId === this.selectedSectionId)
      if (!section) return

      e.preventDefault()
      const startSection = { ...section, vertices: section.vertices ? section.vertices.map(v => ({ ...v })) : undefined }

      if (result.hit === 'body') {
        this.interactionState = { mode: 'moving', handleId: null, vertexIndex: null, startImagePt: pt, startSection }
      } else if (result.hit === 'vertex') {
        this.interactionState = { mode: 'resizing', handleId: null, vertexIndex: result.vertexIndex!, startImagePt: pt, startSection }
      } else if (result.hit === 'handle') {
        this.interactionState = { mode: 'resizing', handleId: result.handleId!, vertexIndex: null, startImagePt: pt, startSection }
      } else if (result.hit === 'rotate') {
        this.interactionState = { mode: 'rotating', handleId: null, vertexIndex: null, startImagePt: pt, startSection }
      }
    }
  }

  private handleMouseMove(e: MouseEvent): void {
    const pt = this.screenToImage(e.clientX, e.clientY)

    // Calibration drag
    if (this.isDraggingCalibration) {
      this.calibrationState.endX = pt.x
      this.calibrationState.endY = this.calibrationState.startY
      this.needsRender = true
      return
    }

    // Drawing preview
    if (this.drawingMode) {
      this.drawingCurrent = pt
      this.needsRender = true
      // For non-polygon drawing, if we have a start point, update preview
      return
    }

    // Section interaction drag
    if (this.interactionState) {
      this.handleInteractionDrag(pt)
      return
    }

    // Hover cursor update
    if (this.selectedSectionId && !this.calibrationState.active) {
      this.updateCursor(pt)
    }
  }

  private handleMouseUp(e: MouseEvent): void {
    // Calibration
    if (this.isDraggingCalibration) {
      this.isDraggingCalibration = false
      const { startX, endX } = this.calibrationState
      if (startX !== null && endX !== null) {
        const canvasLength = Math.abs(endX - startX)
        if (canvasLength > 1 && this.onCalibrationComplete) {
          this.onCalibrationComplete(canvasLength * this.calibrationImagePixelsPerCanvasUnit)
        }
      }
      return
    }

    // Drawing (rect/circle finalize on mouseup)
    if (this.drawingMode && this.drawingMode !== 'draw-polygon') {
      const pt = this.screenToImage(e.clientX, e.clientY)
      this.handleDrawingMouseUp(pt)
      return
    }

    // Interaction
    if (this.interactionState) {
      const section = this.sections.find(s => s.sectionId === this.selectedSectionId)
      if (section && this.onSectionChange) {
        this.onSectionChange({ ...section, vertices: section.vertices ? [...section.vertices] : undefined })
      }
      this.interactionState = null
      this.updateCursor(this.screenToImage(e.clientX, e.clientY))
      return
    }
  }

  private handleMouseEnter(): void {
    this.isPointerInsideCanvas = true
  }

  private handleMouseLeave(): void {
    this.isPointerInsideCanvas = false
    if (!this.drawingMode || !this.drawingCurrent) return

    this.drawingCurrent = null
    this.needsRender = true
  }

  private handleDblClick(e: MouseEvent): void {
    // Polygon: double-click closes the shape
    if (this.drawingMode === 'draw-polygon') {
      // The double-click fires 2 mousedown events before dblclick,
      // each adding an unwanted point. Remove them.
      this.drawingPoints.pop()
      this.drawingPoints.pop()
      if (this.drawingPoints.length >= 3) {
        this.finalizePolygonDrawing()
      }
    }
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.code === 'Space' && this.drawingMode && this.isPointerInsideCanvas) {
      e.preventDefault()
      if (!this.isDrawingMagnifierActive) {
        this.isDrawingMagnifierActive = true
        this.canvas.style.cursor = 'none'
        this.needsRender = true
      }
      return
    }

    // Enter closes polygon
    if (e.key === 'Enter' && this.drawingMode === 'draw-polygon' && this.drawingPoints.length >= 3) {
      this.finalizePolygonDrawing()
    }
    // Escape cancels drawing
    if (e.key === 'Escape' && this.drawingMode) {
      this.cancelDrawing()
    }
  }

  private handleKeyUp(e: KeyboardEvent): void {
    if (e.code !== 'Space' || !this.isDrawingMagnifierActive) return

    this.isDrawingMagnifierActive = false
    this.canvas.style.cursor = this.drawingMode ? 'crosshair' : ''
    this.needsRender = true
  }

  // --- Drawing Mode Handlers ---

  private handleDrawingMouseDown(pt: { x: number; y: number }): void {
    if (this.drawingMode === 'draw-polygon') {
      // Each click adds a vertex
      this.drawingPoints.push(pt)
      this.needsRender = true
      return
    }

    // Rectangle and circle: first click sets start point
    if (this.drawingPoints.length === 0) {
      this.drawingPoints.push(pt)
      this.needsRender = true
    }
  }

  private handleDrawingMouseUp(pt: { x: number; y: number }): void {
    if (this.drawingPoints.length === 0) return

    const start = this.drawingPoints[0]
    const imgScale = this.getImageScale()

    if (this.drawingMode === 'draw-rectangle') {
      const minX = Math.min(start.x, pt.x)
      const maxX = Math.max(start.x, pt.x)
      const minY = Math.min(start.y, pt.y)
      const maxY = Math.max(start.y, pt.y)

      if (maxX - minX < 2 || maxY - minY < 2) {
        this.drawingPoints = []
        return
      }

      const centerMm = this.imageToMm((minX + maxX) / 2, (minY + maxY) / 2)
      const widthMm = (maxX - minX) / imgScale
      const heightMm = (maxY - minY) / imgScale

      if (this.onDrawComplete) {
        this.onDrawComplete({
          shapeType: 'rectangle',
          offsetX: centerMm.mmX,
          offsetY: centerMm.mmY,
          width: widthMm,
          height: heightMm,
        })
      }
      this.cancelDrawing()
    }

    if (this.drawingMode === 'draw-circle') {
      const dx = pt.x - start.x
      const dy = pt.y - start.y
      const radiusPx = Math.sqrt(dx * dx + dy * dy)

      if (radiusPx < 2) {
        this.drawingPoints = []
        return
      }

      const centerMm = this.imageToMm(start.x, start.y)
      const radiusMm = radiusPx / imgScale

      if (this.onDrawComplete) {
        this.onDrawComplete({
          shapeType: 'circle',
          offsetX: centerMm.mmX,
          offsetY: centerMm.mmY,
          width: radiusMm * 2,
          height: radiusMm * 2,
          radius: radiusMm,
        })
      }
      this.cancelDrawing()
    }
  }

  private finalizePolygonDrawing(): void {
    if (this.drawingPoints.length < 3) return

    const imgScale = this.getImageScale()

    // Compute bounding box in image space
    const xs = this.drawingPoints.map(p => p.x)
    const ys = this.drawingPoints.map(p => p.y)
    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    const minY = Math.min(...ys)
    const maxY = Math.max(...ys)

    const centerImgX = (minX + maxX) / 2
    const centerImgY = (minY + maxY) / 2
    const centerMm = this.imageToMm(centerImgX, centerImgY)
    const widthMm = (maxX - minX) / imgScale
    const heightMm = (maxY - minY) / imgScale

    // Convert vertices to mm relative to center
    const vertices = this.drawingPoints.map(p => ({
      x: (p.x - centerImgX) / imgScale,
      y: -(p.y - centerImgY) / imgScale, // flip Y
    }))

    if (this.onDrawComplete) {
      this.onDrawComplete({
        shapeType: 'polygon',
        offsetX: centerMm.mmX,
        offsetY: centerMm.mmY,
        width: widthMm,
        height: heightMm,
        vertices,
      })
    }
    this.cancelDrawing()
  }

  // --- Interaction Handlers ---

  private handleInteractionDrag(pt: { x: number; y: number }): void {
    if (!this.interactionState) return

    const section = this.sections.find(s => s.sectionId === this.selectedSectionId)
    if (!section) return

    const { mode, startImagePt, startSection, handleId } = this.interactionState
    const imgScale = this.getImageScale()
    const dx = pt.x - startImagePt.x
    const dy = pt.y - startImagePt.y

    if (mode === 'moving') {
      const dmmX = dx / imgScale
      const dmmY = -dy / imgScale // Y inverted
      section.offsetX = startSection.offsetX + dmmX
      section.offsetY = startSection.offsetY + dmmY
    }

    if (mode === 'resizing' && this.interactionState.vertexIndex != null) {
      this.handleVertexDrag(section, startSection, this.interactionState.vertexIndex, dx, dy, imgScale)
    } else if (mode === 'resizing' && handleId) {
      this.handleResizeDrag(section, startSection, handleId, dx, dy, imgScale)
    }

    if (mode === 'rotating') {
      const bounds = this.getSectionBounds(startSection)
      if (bounds) {
        const cx = bounds.x + bounds.width / 2
        const cy = bounds.y + bounds.height / 2
        const angle = Math.atan2(pt.x - cx, -(pt.y - cy)) * 180 / Math.PI
        section.rotation = Math.round(angle)
      }
    }

    this.needsRender = true
  }

  private handleResizeDrag(
    section: Section2D, start: Section2D, handleId: HandleId,
    dx: number, dy: number, imgScale: number
  ): void {
    const dmmX = dx / imgScale
    const dmmY = -dy / imgScale
    const shapeType = section.shapeType || 'rectangle'

    let newW = start.width
    let newH = start.height
    let newOffX = start.offsetX
    let newOffY = start.offsetY

    // Which edges are being dragged
    const isLeft = handleId === 'nw' || handleId === 'sw' || handleId === 'w'
    const isRight = handleId === 'ne' || handleId === 'se' || handleId === 'e'
    const isTop = handleId === 'nw' || handleId === 'ne' || handleId === 'n'
    const isBottom = handleId === 'sw' || handleId === 'se' || handleId === 's'

    if (isRight) {
      newW = Math.max(1, start.width + dmmX)
      newOffX = start.offsetX + dmmX / 2
    }
    if (isLeft) {
      newW = Math.max(1, start.width - dmmX)
      newOffX = start.offsetX + dmmX / 2
    }
    if (isTop) {
      // Canvas top = mm top (Y inverted), dragging up increases offsetY
      newH = Math.max(1, start.height + dmmY)
      newOffY = start.offsetY + dmmY / 2
    }
    if (isBottom) {
      newH = Math.max(1, start.height - dmmY)
      newOffY = start.offsetY - dmmY / 2
    }

    // Circle: constrain to equal dimensions
    if (shapeType === 'circle') {
      const size = Math.max(newW, newH)
      newW = size
      newH = size
      section.radius = size / 2
    }

    section.width = newW
    section.height = newH
    section.offsetX = newOffX
    section.offsetY = newOffY
  }

  private handleVertexDrag(
    section: Section2D, start: Section2D, vertexIndex: number,
    dx: number, dy: number, imgScale: number
  ): void {
    if (!start.vertices || !section.vertices) return

    const dmmX = dx / imgScale
    const dmmY = -dy / imgScale

    // Move the dragged vertex
    const newVertices = start.vertices.map((v, i) =>
      i === vertexIndex ? { x: v.x + dmmX, y: v.y + dmmY } : { ...v }
    )

    // Recompute bounding box from all vertices
    const xs = newVertices.map(v => v.x)
    const ys = newVertices.map(v => v.y)
    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    const minY = Math.min(...ys)
    const maxY = Math.max(...ys)
    const newW = maxX - minX
    const newH = maxY - minY
    const newCenterX = (minX + maxX) / 2
    const newCenterY = (minY + maxY) / 2

    // Shift vertices so they remain relative to the new center
    section.vertices = newVertices.map(v => ({
      x: v.x - newCenterX,
      y: v.y - newCenterY,
    }))

    // Update bounding box and offset to match new center
    section.width = Math.max(1, newW)
    section.height = Math.max(1, newH)
    section.offsetX = start.offsetX + newCenterX
    section.offsetY = start.offsetY + newCenterY
  }

  // --- Cursor ---

  private updateCursor(pt: { x: number; y: number }): void {
    if (this.drawingMode || this.calibrationState.active) {
      this.canvas.style.cursor = 'crosshair'
      return
    }

    const result = this.hitTest(pt.x, pt.y)
    switch (result.hit) {
      case 'body':
        this.canvas.style.cursor = 'move'
        break
      case 'rotate':
        this.canvas.style.cursor = 'crosshair'
        break
      case 'vertex':
        this.canvas.style.cursor = 'nwse-resize'
        break
      case 'handle': {
        const cursorMap: Record<string, string> = {
          nw: 'nwse-resize', se: 'nwse-resize',
          ne: 'nesw-resize', sw: 'nesw-resize',
          n: 'ns-resize', s: 'ns-resize',
          e: 'ew-resize', w: 'ew-resize',
        }
        this.canvas.style.cursor = cursorMap[result.handleId!] || 'default'
        break
      }
      default:
        this.canvas.style.cursor = 'default'
    }
  }

  // --- Rendering ---

  private startRenderLoop(): void {
    const animate = () => {
      this.animFrameId = requestAnimationFrame(animate)
      if (this.needsRender) {
        this.render()
        this.needsRender = false
      }
    }
    animate()
  }

  private stopRenderLoop(): void {
    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId)
      this.animFrameId = null
    }
  }

  private render(): void {
    const ctx = this.ctx
    if (!ctx) return

    const rect = this.canvas.getBoundingClientRect()
    const clientWidth = rect.width
    const clientHeight = rect.height

    if (!isFinite(this.viewTransform.x) || !isFinite(this.viewTransform.y) ||
        !isFinite(this.viewTransform.scale) || this.viewTransform.scale <= 0) {
      this.fitImageToView()
    }

    ctx.clearRect(0, 0, clientWidth, clientHeight)
    ctx.fillStyle = '#f3f4f6'
    ctx.fillRect(0, 0, clientWidth, clientHeight)

    ctx.save()
    ctx.translate(this.viewTransform.x, this.viewTransform.y)
    ctx.scale(this.viewTransform.scale, this.viewTransform.scale)

    const base = this.getBaseSize()

    // Draw product image or target object rectangle
    if (this.productImage && this.productImageLoaded &&
        this.productImage.width > 0 && this.productImage.height > 0) {
      ctx.drawImage(this.productImage, 0, 0)
    } else if (base.width > 0 && base.height > 0) {
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, base.width, base.height)

      if (this.selectedSectionId) {
        const sectionImg = this.sectionImages.get(this.selectedSectionId)
        if (sectionImg && sectionImg.complete && sectionImg.naturalWidth > 0) {
          ctx.drawImage(sectionImg, 0, 0, base.width, base.height)
        }
      }
    }

    // Draw only the selected section (hide during drawing mode — preview replaces it)
    if (!this.drawingMode) {
      for (const section of this.sections) {
        if (section.sectionId !== this.selectedSectionId) continue
        this.drawSection(ctx, section, true)
      }
    }

    // Drawing preview
    if (this.drawingMode) {
      this.drawDrawingPreview(ctx)
    }

    ctx.restore()

    if (this.drawingMode && this.isDrawingMagnifierActive) {
      this.drawDrawingMagnifier()
    }

    // Calibration line on top
    if (this.calibrationState.active) {
      this.drawCalibrationLine()
    }
  }

  private drawCalibrationLine(): void {
    const { startX, startY, endX, endY } = this.calibrationState
    if (startX === null || startY === null || endX === null || endY === null) return

    const ctx = this.ctx
    ctx.save()
    ctx.translate(this.viewTransform.x, this.viewTransform.y)
    ctx.scale(this.viewTransform.scale, this.viewTransform.scale)

    const vs = this.viewTransform.scale
    const lineWidth = SELECTED_SECTION_STROKE_WIDTH_PX / vs

    ctx.beginPath()
    ctx.moveTo(startX, startY)
    ctx.lineTo(endX, endY)
    ctx.strokeStyle = '#ef4444'
    ctx.lineWidth = lineWidth
    ctx.setLineDash([])
    ctx.stroke()

    const tickHeight = 8 / vs
    ctx.beginPath()
    ctx.moveTo(startX, startY - tickHeight)
    ctx.lineTo(startX, startY + tickHeight)
    ctx.moveTo(endX, endY - tickHeight)
    ctx.lineTo(endX, endY + tickHeight)
    ctx.stroke()

    const midX = (startX + endX) / 2
    const fontSize = CALIBRATION_LABEL_FONT_SIZE_PX / vs
    const imagePixelLength = Math.abs(endX - startX) * this.calibrationImagePixelsPerCanvasUnit
    ctx.font = `bold ${fontSize}px sans-serif`
    ctx.fillStyle = '#ef4444'
    ctx.textAlign = 'center'
    ctx.fillText(
      `${imagePixelLength.toFixed(0)}px`,
      midX,
      startY - tickHeight - 4 / vs
    )

    ctx.restore()
  }

  private drawSection(ctx: CanvasRenderingContext2D, section: Section2D, isSelected: boolean): void {
    const bounds = this.getSectionBounds(section)
    if (!bounds) return
    if (bounds.width <= 0 || bounds.height <= 0) return

    ctx.save()

    const vs = this.viewTransform.scale
    const cx = bounds.x + bounds.width / 2
    const cy = bounds.y + bounds.height / 2
    const rotation = (section.rotation || 0) * Math.PI / 180

    // Apply rotation around section center
    if (rotation !== 0) {
      ctx.translate(cx, cy)
      ctx.rotate(rotation)
      ctx.translate(-cx, -cy)
    }

    const shapeType = section.shapeType || 'rectangle'

    // The section body is a selection highlight only. The material is NOT painted
    // over the whole section area — a section is the region a shopper may engrave,
    // not an engraved surface, so tinting all of it misrepresents the result.
    // The material preview goes on the section label instead (see below), which is
    // the only mark-shaped thing this editor has to stand in for real artwork.
    const matType = section.materialType || 'steel'
    const engravingTint = clamp(section.engravingTint ?? 0.5, 0, 1)
    const opacity = section.overlayOpacity ?? 1.0
    const fillColor = isSelected ? `rgba(59, 130, 246, 0.08)` : 'transparent'
    const strokeColor = isSelected ? '#3b82f6' : 'rgba(107, 114, 128, 0.7)'
    // Screen-constant border width so it stays thin at any zoom level.
    // (A fixed world-space width grows with zoom — most noticeable on polygons.)
    const lineWidth = (isSelected ? SELECTED_SECTION_STROKE_WIDTH_PX : 1) / vs

    ctx.fillStyle = fillColor
    ctx.strokeStyle = strokeColor
    ctx.lineWidth = lineWidth
    // Round joins/caps prevent long miter spikes at sharp polygon corners.
    ctx.lineJoin = 'round'
    ctx.lineCap = 'round'
    ctx.setLineDash(isSelected ? [] : [4 / vs, 4 / vs])

    switch (shapeType) {
      case 'rectangle':
        ctx.fillRect(bounds.x, bounds.y, bounds.width, bounds.height)
        ctx.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height)
        break

      case 'circle': {
        const r = Math.min(bounds.width, bounds.height) / 2
        ctx.beginPath()
        ctx.arc(cx, cy, r, 0, Math.PI * 2)
        ctx.fill()
        ctx.stroke()
        break
      }

      case 'polygon': {
        if (section.vertices && section.vertices.length >= 3) {
          const imgScale = this.getImageScale()
          ctx.beginPath()
          for (let i = 0; i < section.vertices.length; i++) {
            const v = section.vertices[i]
            const vx = cx + v.x * imgScale
            const vy = cy - v.y * imgScale
            if (i === 0) ctx.moveTo(vx, vy)
            else ctx.lineTo(vx, vy)
          }
          ctx.closePath()
          ctx.fill()
          ctx.stroke()
        } else {
          // Fallback: draw as rectangle
          ctx.fillRect(bounds.x, bounds.y, bounds.width, bounds.height)
          ctx.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height)
        }
        break
      }
    }

    // Draw handles if selected (in rotated space so they follow the shape)
    if (isSelected) {
      this.drawHandles(ctx, bounds, section)
    }

    ctx.restore()

    // Section label — always horizontal (drawn outside rotation context)
    // Compute horizontal width available based on rotated section extent
    const absC = Math.abs(Math.cos(rotation))
    const absS = Math.abs(Math.sin(rotation))
    const horizAvail = bounds.width * absC + bounds.height * absS
    const vertAvail = bounds.width * absS + bounds.height * absC

    ctx.save()
    const label = section.name || 'Section'
    const minDim = Math.min(horizAvail, vertAvail)
    const standardFontSize = Math.max(8 / vs, Math.min(minDim * 0.15, minDim * 0.5))
    const fontWeight = isSelected ? 'bold ' : ''
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    // Selected section: paint the label as though it were engraved in the chosen
    // material, so the dropdown has a live preview. Unselected labels stay neutral
    // grey — with several sections on screen the editor has to stay readable.
    const material = isSelected
      ? getMaterialAppearance(matType, engravingTint, opacity, section.materialColor)
      : null

    if (material) {
      ctx.fillStyle = material.color
      ctx.globalAlpha = material.alpha
      // Near-white materials (sandblast, glass etch) need some separation from a
      // light product photo. Keep it to a hairline — at any real thickness the
      // halo bleeds past the glyph and reads as a drop shadow rather than an edge.
      ctx.strokeStyle = 'rgba(17, 24, 39, 0.30)'
      ctx.lineJoin = 'round'
      ctx.miterLimit = 2
    } else {
      ctx.fillStyle = isSelected ? '#3b82f6' : 'rgba(107, 114, 128, 0.9)'
    }

    // A custom material paints its uploaded tile into the glyphs themselves.
    // The tile is scaled to roughly the cap height: at its native 512px it would
    // cover the whole label with a single flat sample and read as a colour wash
    // rather than a material. Falls through to the flat colour until it loads.
    const applyMaterialPattern = (size: number) => {
      if (!material) return

      // Custom materials use their uploaded tile; the built-ins use the same
      // procedural grain the customizer generates, so sandblast reads as frosted
      // here instead of collapsing to flat white on a light product.
      let tile: CanvasImageSource | null = null
      let tileWidth = 0

      if (matType === 'custom') {
        const uploaded = section.materialFile
          ? this.materialPatterns.get(section.materialFile)
          : null
        if (uploaded && uploaded.complete && uploaded.naturalWidth > 0) {
          tile = uploaded
          tileWidth = uploaded.naturalWidth
        }
      } else {
        const generated = getMaterialTexture(matType)
        if (generated) {
          tile = generated
          tileWidth = generated.width
        }
      }

      if (!tile || !tileWidth) return

      const pattern = ctx.createPattern(tile, 'repeat')
      if (!pattern) return
      pattern.setTransform(new DOMMatrix().scaleSelf((size * 1.5) / tileWidth))
      ctx.fillStyle = pattern
    }

    const paintLabel = (text: string, x: number, y: number) => {
      if (material) ctx.strokeText(text, x, y)
      ctx.fillText(text, x, y)
    }

    // Shrink font size down to half of standard if text overflows
    let fontSize = standardFontSize
    const minFontSize = standardFontSize * 0.5
    ctx.font = `${fontWeight}${fontSize}px sans-serif`
    let textWidth = ctx.measureText(label).width
    if (textWidth > horizAvail * 0.85 && label.length > 6) {
      const shrunkSize = Math.max(minFontSize, fontSize * (horizAvail * 0.85) / textWidth)
      fontSize = shrunkSize
      ctx.font = `${fontWeight}${fontSize}px sans-serif`
      textWidth = ctx.measureText(label).width
    }

    // If still too wide at half size, split to two lines
    if (textWidth > horizAvail * 0.85 && label.length > 6) {
      const mid = Math.ceil(label.length / 2)
      let splitIdx = mid
      const searchRange = 3
      for (let offset = 0; offset <= searchRange; offset++) {
        for (const idx of [mid + offset, mid - offset]) {
          if (idx > 0 && idx < label.length && /[\s_-]/.test(label[idx])) {
            splitIdx = idx
            offset = searchRange + 1 // break outer
            break
          }
        }
      }
      const line1 = label.slice(0, splitIdx)
      const line2 = label.slice(splitIdx + (label[splitIdx] === ' ' ? 1 : 0))
      const lineSpacing = fontSize * 0.6
      ctx.lineWidth = fontSize * 0.05
      applyMaterialPattern(fontSize)
      paintLabel(line1, cx, cy - lineSpacing)
      paintLabel(line2, cx, cy + lineSpacing)
    } else {
      ctx.lineWidth = fontSize * 0.05
      applyMaterialPattern(fontSize)
      paintLabel(label, cx, cy)
    }
    ctx.restore()
  }

  private drawScreenSpaceHandles(
    ctx: CanvasRenderingContext2D,
    points: { x: number; y: number }[],
    style: ScreenHandleStyle
  ): void {
    if (points.length === 0) return

    const dpr = window.devicePixelRatio || 1
    const worldTransform = ctx.getTransform()
    const deviceScale = Math.max(
      Math.hypot(worldTransform.a, worldTransform.b),
      Number.EPSILON
    )
    const handleSize = this.getHandleMetrics().visualSizePx * dpr
    const handleRadius = handleSize / 2
    const handleHalfSizeInWorld = handleRadius / deviceScale
    const toDevicePoint = (x: number, y: number) => ({
      x: worldTransform.a * x + worldTransform.c * y + worldTransform.e,
      y: worldTransform.b * x + worldTransform.d * y + worldTransform.f,
    })

    ctx.save()
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.fillStyle = style.fillStyle
    if (style.strokeStyle) ctx.strokeStyle = style.strokeStyle
    ctx.lineWidth = dpr
    ctx.setLineDash([])

    for (const point of points) {
      if (style.shape === 'circle') {
        const screenPoint = toDevicePoint(point.x, point.y)
        ctx.beginPath()
        ctx.arc(screenPoint.x, screenPoint.y, handleRadius, 0, Math.PI * 2)
      } else {
        const corners = [
          toDevicePoint(point.x - handleHalfSizeInWorld, point.y - handleHalfSizeInWorld),
          toDevicePoint(point.x + handleHalfSizeInWorld, point.y - handleHalfSizeInWorld),
          toDevicePoint(point.x + handleHalfSizeInWorld, point.y + handleHalfSizeInWorld),
          toDevicePoint(point.x - handleHalfSizeInWorld, point.y + handleHalfSizeInWorld),
        ]

        ctx.beginPath()
        ctx.moveTo(corners[0].x, corners[0].y)
        for (let index = 1; index < corners.length; index++) {
          ctx.lineTo(corners[index].x, corners[index].y)
        }
        ctx.closePath()
      }

      ctx.fill()
      if (style.strokeStyle) ctx.stroke()
    }

    ctx.restore()
  }

  private drawScreenSpaceLine(
    ctx: CanvasRenderingContext2D,
    start: { x: number; y: number },
    end: { x: number; y: number },
    color: string
  ): void {
    const dpr = window.devicePixelRatio || 1
    const worldTransform = ctx.getTransform()
    const toDevicePoint = (point: { x: number; y: number }) => ({
      x: worldTransform.a * point.x + worldTransform.c * point.y + worldTransform.e,
      y: worldTransform.b * point.x + worldTransform.d * point.y + worldTransform.f,
    })
    const screenStart = toDevicePoint(start)
    const screenEnd = toDevicePoint(end)

    ctx.save()
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.beginPath()
    ctx.moveTo(screenStart.x, screenStart.y)
    ctx.lineTo(screenEnd.x, screenEnd.y)
    ctx.strokeStyle = color
    ctx.lineWidth = dpr
    ctx.setLineDash([])
    ctx.stroke()
    ctx.restore()
  }

  private drawHandles(ctx: CanvasRenderingContext2D, bounds: SectionBounds, section?: Section2D): void {
    const handleMetrics = this.getHandleMetrics()
    const cx = bounds.x + bounds.width / 2
    const cy = bounds.y + bounds.height / 2
    const shapeType = section?.shapeType || 'rectangle'
    const style: ScreenHandleStyle = {
      shape: shapeType === 'polygon' ? 'circle' : 'square',
      fillStyle: '#ffffff',
      strokeStyle: '#3b82f6',
    }
    let positions: { x: number; y: number }[]

    if (shapeType === 'polygon' && section?.vertices && section.vertices.length >= 3) {
      const imgScale = this.getImageScale()
      positions = section.vertices.map((vertex) => ({
        x: cx + vertex.x * imgScale,
        y: cy - vertex.y * imgScale,
      }))
    } else {
      positions = [
        { x: bounds.x, y: bounds.y },
        { x: bounds.x + bounds.width, y: bounds.y },
        { x: bounds.x, y: bounds.y + bounds.height },
        { x: bounds.x + bounds.width, y: bounds.y + bounds.height },
        { x: cx, y: bounds.y },
        { x: cx, y: bounds.y + bounds.height },
        { x: bounds.x, y: cy },
        { x: bounds.x + bounds.width, y: cy },
      ]
    }

    this.drawScreenSpaceHandles(ctx, positions, style)

    const rotHandleY = bounds.y - handleMetrics.rotationDistance
    this.drawScreenSpaceLine(
      ctx,
      { x: cx, y: bounds.y },
      { x: cx, y: rotHandleY },
      '#3b82f6'
    )
    this.drawScreenSpaceHandles(ctx, [{ x: cx, y: rotHandleY }], {
      ...style,
      shape: 'circle',
    })
  }

  private drawDrawingPreview(ctx: CanvasRenderingContext2D): void {
    if (!this.drawingCurrent) return

    const vs = this.viewTransform.scale
    const lineWidth = SELECTED_SECTION_STROKE_WIDTH_PX / vs

    ctx.strokeStyle = '#10b981'
    ctx.lineWidth = lineWidth
    ctx.lineJoin = 'round'
    ctx.lineCap = 'round'
    ctx.setLineDash([3 / vs, 3 / vs])
    ctx.fillStyle = 'rgba(16, 185, 129, 0.08)'

    if (this.drawingMode === 'draw-rectangle' && this.drawingPoints.length === 1) {
      const start = this.drawingPoints[0]
      const w = this.drawingCurrent.x - start.x
      const h = this.drawingCurrent.y - start.y
      ctx.fillRect(start.x, start.y, w, h)
      ctx.strokeRect(start.x, start.y, w, h)
    }

    if (this.drawingMode === 'draw-circle' && this.drawingPoints.length === 1) {
      const start = this.drawingPoints[0]
      const dx = this.drawingCurrent.x - start.x
      const dy = this.drawingCurrent.y - start.y
      const r = Math.sqrt(dx * dx + dy * dy)
      ctx.beginPath()
      ctx.arc(start.x, start.y, r, 0, Math.PI * 2)
      ctx.fill()
      ctx.stroke()
    }

    if (this.drawingMode === 'draw-polygon' && this.drawingPoints.length > 0) {
      ctx.beginPath()
      ctx.moveTo(this.drawingPoints[0].x, this.drawingPoints[0].y)
      for (let i = 1; i < this.drawingPoints.length; i++) {
        ctx.lineTo(this.drawingPoints[i].x, this.drawingPoints[i].y)
      }
      ctx.lineTo(this.drawingCurrent.x, this.drawingCurrent.y)
      if (this.drawingPoints.length >= 3) {
        ctx.closePath()
        ctx.fill()
      }
      ctx.stroke()

      this.drawScreenSpaceHandles(ctx, this.drawingPoints, {
        shape: 'circle',
        fillStyle: '#10b981',
      })
    }
  }

  private drawDrawingMagnifier(): void {
    if (!this.drawingCurrent) return

    const ctx = this.ctx
    const dpr = window.devicePixelRatio || 1
    const size = DRAWING_MAGNIFIER_SIZE_PX
    const radius = size / 2
    const sourceSize = size / DRAWING_MAGNIFIER_ZOOM
    const screenX = this.viewTransform.x + this.drawingCurrent.x * this.viewTransform.scale
    const screenY = this.viewTransform.y + this.drawingCurrent.y * this.viewTransform.scale

    ctx.save()

    ctx.beginPath()
    ctx.arc(screenX, screenY, radius, 0, Math.PI * 2)
    ctx.clip()
    ctx.drawImage(
      this.canvas,
      (screenX - sourceSize / 2) * dpr,
      (screenY - sourceSize / 2) * dpr,
      sourceSize * dpr,
      sourceSize * dpr,
      screenX - radius,
      screenY - radius,
      size,
      size
    )

    ctx.restore()
    ctx.save()

    ctx.beginPath()
    ctx.arc(screenX, screenY, radius - 1, 0, Math.PI * 2)
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 4
    ctx.stroke()

    ctx.beginPath()
    ctx.arc(screenX, screenY, radius - 1, 0, Math.PI * 2)
    ctx.strokeStyle = '#10b981'
    ctx.lineWidth = 2
    ctx.stroke()

    const crosshairSize = 7
    ctx.lineCap = 'round'

    ctx.beginPath()
    ctx.moveTo(screenX - crosshairSize, screenY)
    ctx.lineTo(screenX + crosshairSize, screenY)
    ctx.moveTo(screenX, screenY - crosshairSize)
    ctx.lineTo(screenX, screenY + crosshairSize)
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 4
    ctx.stroke()

    ctx.beginPath()
    ctx.moveTo(screenX - crosshairSize, screenY)
    ctx.lineTo(screenX + crosshairSize, screenY)
    ctx.moveTo(screenX, screenY - crosshairSize)
    ctx.lineTo(screenX, screenY + crosshairSize)
    ctx.strokeStyle = '#10b981'
    ctx.lineWidth = 2
    ctx.stroke()

    ctx.restore()
  }

  private handleResize(): void {
    const rect = this.canvas.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return
    const dpr = window.devicePixelRatio || 1
    this.canvas.width = rect.width * dpr
    this.canvas.height = rect.height * dpr
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    this.needsRender = true
  }

}

export default TwoDCanvas
